<template>
  <div class="stp-tooltip-settings">
    <!-- Header -->
    <div class="component-header">
      <div class="info">
        <div v-safe-html="t('stp_teaching_action_info_text')"></div>
      </div>
    </div>

    <div class="component-content">
      <cv-form class="form-group teacher-actions" @submit.prevent>
        <!-- Teaching Action Name -->
        <div class="wrapper teaching-area">
          <div v-if="isRegisteredTeachingAction" class="field-set">
            <!-- Registered teaching action, only for display purpose -->
            <div class="field-label">{{ t("stp_teaching_action_title") }}</div>
            <div class="field-value">{{ stpData.teachingActionName }}</div>
          </div>
          <dl v-else class="field-set type-text">
            <!-- Custom teaching action -->
            <dt class="field-label">{{ t("stp_teaching_action_name_label") }}</dt>
            <dd class="field-value">
              <text-input
                ref="stpTeachingActionInput"
                :mandatory="true"
                :character-limit="teachingActionNameCharLimit"
                :placeholder="t('stp_teaching_action_name_input_placeholder')"
                :value="stpData.teachingActionName"
                :disabled="isInputDisabled"
                :do-validate="doValidateTeachingAction"
                @change="handleCustomTaChanged"
                @validated="handleTaValidated"
              />
            </dd>
          </dl>
        </div>

        <!-- STP notes -->
        <div class="wrapper notes">
          <dl class="field-set type-text">
            <dt class="field-label">{{ t("stp_teaching_action_notes_label") }}</dt>
            <dd class="field-value">
              <text-area
                ref="taNotesInput"
                :character-limit="teachingActionNotesCharLimit"
                :placeholder="t('stp_teaching_action_notes_input_placeholder')"
                :value="stpData.teachingActionNotes"
                :disabled="isInputDisabled"
                @change="handleTaNotesChanged"
              />
            </dd>
          </dl>
        </div>
      </cv-form>
    </div>
  </div>
</template>

<script setup lang="ts">
import stpApiService from "@apis/stp.api";
import { isEmpty } from "@utils/var.check";
import DOMUtils from "@utils/dom.utils";
import { ref, computed, watch, onMounted, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

// Props
const props = defineProps({
  task: { type: String, default: "" },
  selectedStp: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["isDirty", "validated", "saveData"]);

// Data
const teachingActionNameCharLimit = ref(50);
const teachingActionNotesCharLimit = ref(500);
const dirty = ref(false);
const stpData = ref({ ...props.selectedStp });
const doValidateTeachingAction = ref(false);

const stpTeachingActionInput = useTemplateRef("stpTeachingActionInput");
const taNotesInput = useTemplateRef("taNotesInput");

const { t } = useI18n();

// Computed
const isRegisteredTeachingAction = computed(() => {
  return !!stpData.value.teachingActionId;
});

const isInputDisabled = computed(() => props.task !== "");

// Methods
const isCreate = () => !props.selectedStp;

const setDirty = () => {
  dirty.value = true;
};

const handleCustomTaChanged = (newValue) => {
  setDirty();
  stpData.value.teachingActionName = newValue;
};

const handleTaNotesChanged = (newValue) => {
  setDirty();
  stpData.value.teachingActionNotes = newValue;
};

const handleTaValidated = () => {
  doValidateTeachingAction.value = false;
};

const validate = () => {
  return validateTeachingAction() && validateNotes();
};

const validateTeachingAction = () => {
  const teachingActionName = stpData.value.teachingActionName ?? "";

  if (isCreate() && !dirty.value) {
    doValidateTeachingAction.value = true;
  }

  if (dirty.value) {
    doValidateTeachingAction.value = true;
  }

  return (
    teachingActionName.trim().length > 0 &&
    teachingActionName.length <= teachingActionNameCharLimit.value
  );
};

const validateNotes = () => {
  return taNotesInput.value?.validateMaxLimit();
};

const formData = () => ({
  activeTab: "teaching-action-tab-other",
  teachingActionId: stpData.value.teachingActionId,
  teachingActionUrl: stpData.value.teachingActionUrl,
  teachingActionName: stpData.value.teachingActionName,
  teachingActionNotes: stpData.value.teachingActionNotes ?? "",
});

const handleLegacyDataUpdate = () => {
  // target for data update is previously registered teaching action.
  if (!isRegisteredTeachingAction.value) return;

  const currentTeachingActionId = parseInt(stpData.value.teachingActionId);

  stpApiService.fetchTeachingActions().then((response) => {
    if (response.code !== "ok") return;

    // find the teaching action
    const teachingAction = response.data.find((ta) => ta.id === currentTeachingActionId);

    if (isEmpty(teachingAction)) {
      // case when teaching action has been deleted
      // we will convert to current teaching action to a custom one
      stpData.value.teachingActionId = null;
      stpData.value.teachingActionUrl = null;
      return;
    }

    if (
      stpData.value.teachingActionUrl !== teachingAction.url ||
      stpData.value.teachingActionName !== teachingAction.name
    ) {
      // case when url or name has changed, update.
      stpData.value.teachingActionUrl = teachingAction.url;
      stpData.value.teachingActionName = teachingAction.name;
    }
  });
};

const focusTeachingAction = () => {
  const input = DOMUtils.elOf(stpTeachingActionInput.value?.$el, "input")?.[0];
  input?.focus();
};

// Watch
watch(
  () => props.task,
  (newTask) => {
    if (newTask === "checkDirty") {
      emit("isDirty", dirty.value);
    } else if (newTask === "validate") {
      emit("validated", {
        code: validate() ? "ok" : "error",
      });
    } else if (newTask === "save") {
      emit("saveData", {
        code: "ok",
        data: formData(),
      });
    }
  },
);

// Auto convert legacy data
handleLegacyDataUpdate();

// Mounted
onMounted(() => {
  focusTeachingAction();
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/spacing";

.stp-tooltip-settings {
  > .component-header {
    > .info {
      padding-bottom: $spacing-05;
    }
  }
}
</style>
